/**
 * Custom hook for managing pitch deck generation state
 */

import { useState, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "@/hooks/use-toast";

export interface PitchDeck {
  id: string;
  user_id: string;
  form_id: string;
  file_key: string;
  file_url: string | null;
  file_size: number | null;
  status: "pending" | "generating" | "completed" | "failed";
  template_version: string;
  version: number;
  is_current: boolean;
  generation_started_at: string | null;
  generation_completed_at: string | null;
  generation_duration_seconds: number | null;
  error_message: string | null;
  created_at: string;
  share_token: string | null;
  shared_at: string | null;
  share_expires_at: string | null;
}

interface PitchDeckGenerationState {
  formsComplete: boolean;
  currentDeck: PitchDeck | null;
}

/**
 * Query key for the generation page state. usePitchDeckSharing invalidates this
 * key after generating/revoking a share token, so it must stay in sync.
 */
export const PITCH_DECK_GENERATION_KEY = "pitch-deck-generation";

export function usePitchDeckGeneration() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [generating, setGenerating] = useState(false);

  const { data, isLoading } = useQuery<PitchDeckGenerationState>({
    queryKey: [PITCH_DECK_GENERATION_KEY, user?.id],
    enabled: !!user,
    queryFn: async () => {
      // Check if forms are complete
      const { data: formData, error: formError } = await supabase
        .from("pitch_deck_forms")
        .select("completed_at, form_1_completed_at, form_2_completed_at")
        .eq("user_id", user!.id)
        .maybeSingle();

      if (formError) throw formError;

      if (!formData?.completed_at) {
        return { formsComplete: false, currentDeck: null };
      }

      // Check for existing pitch deck
      const { data: deckData, error: deckError } = await supabase
        .from("pitch_decks")
        .select("*")
        .eq("user_id", user!.id)
        .eq("is_current", true)
        .maybeSingle();

      if (deckError) throw deckError;

      return {
        formsComplete: true,
        currentDeck: (deckData as PitchDeck | null) ?? null,
      };
    },
  });

  const formsComplete = data?.formsComplete ?? false;
  const currentDeck = data?.currentDeck ?? null;
  // Version 1 can regenerate once to version 2
  const canRegenerate = currentDeck?.version === 1 && currentDeck?.status === "completed";

  const checkStatus = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [PITCH_DECK_GENERATION_KEY, user?.id],
    });
  }, [queryClient, user?.id]);

  const handleGenerate = useCallback(async (isRegeneration = false) => {
    if (!user) {
      toast({
        title: "Error",
        description: "Please log in to continue",
        variant: "destructive",
      });
      return false;
    }

    setGenerating(true);

    try {
      // NOTE: archiving the previous deck and logging the `regenerated`
      // analytics event both happen inside the edge function, in the same
      // success path that inserts the replacement row. Doing it here meant a
      // failed regeneration permanently hid a working deck.

      // Get fresh session with error handling
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !sessionData.session) {
        toast({
          title: "Session Expired",
          description: "Please log in again to continue",
          variant: "destructive",
        });
        setGenerating(false);
        return false;
      }

      console.log("Calling Edge Function with auth token");

      // Build URL - use the Supabase URL from environment
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const functionUrl = `${supabaseUrl}/functions/v1/generate-pitch-deck`;

      // Call Edge Function with all required headers
      const response = await fetch(functionUrl, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${sessionData.session.access_token}`,
          "Content-Type": "application/json",
          "apikey": supabaseAnonKey,
        },
      });

      console.log("Response status:", response.status);

      const result = await response.json();
      console.log("Result:", result);

      if (!response.ok || !result.success) {
        throw new Error(result.message || result.error || "Generation failed");
      }

      toast({
        title: "Success!",
        description: "Your pitch deck has been generated successfully!",
      });

      // Reload status
      await checkStatus();

      return true;
    } catch (error: any) {
      console.error("Generation error:", error);
      toast({
        title: "Generation Failed",
        description: error.message || "Failed to generate pitch deck. Please try again.",
        variant: "destructive",
      });
      // Re-read state so the UI reflects whatever the server actually committed.
      await checkStatus();
      return false;
    } finally {
      setGenerating(false);
    }
  }, [user, checkStatus]);

  const handleDownload = useCallback(async () => {
    if (!currentDeck?.file_key || !user) return;

    try {
      // Mint a FRESH signed URL at click time. The stored file_url was a
      // one-hour signed URL, so persisted links died an hour after generation.
      const { data: signed, error: signError } = await supabase.storage
        .from("pitch-decks")
        .createSignedUrl(currentDeck.file_key, 60);

      if (signError || !signed?.signedUrl) {
        console.error("Failed to create signed URL:", signError);
        toast({
          title: "Download Failed",
          description: "Could not prepare your download. Please try again.",
          variant: "destructive",
        });
        return;
      }

      const opened = window.open(signed.signedUrl, "_blank");

      if (!opened) {
        toast({
          title: "Download Blocked",
          description: "Your browser blocked the download. Please allow pop-ups and try again.",
          variant: "destructive",
        });
        return;
      }

      // Only log analytics once the download actually started
      await supabase.from("pitch_deck_analytics").insert({
        pitch_deck_id: currentDeck.id,
        user_id: user.id,
        event_type: "downloaded",
        metadata: { file_size: currentDeck.file_size },
      });

      toast({
        title: "Download Started",
        description: "Your pitch deck is downloading.",
      });
    } catch (error) {
      console.error("Download error:", error);
      toast({
        title: "Download Failed",
        description: "Failed to download. Please try again.",
        variant: "destructive",
      });
    }
  }, [currentDeck, user]);

  return {
    loading: isLoading,
    generating,
    currentDeck,
    canRegenerate,
    formsComplete,
    handleGenerate,
    handleDownload,
    checkStatus,
  };
}
